import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, PenLine, BookOpen, LogIn, LogOut } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MILESTONE_MSG =
  "Seven days of showing up. You are building a beautiful rhythm of care for yourself. Ready to begin your next week?";

const PROMPTS = [
  "What is one rhythm your body needs today?",
  "Where did you feel most at ease in the last day?",
  "Name one thing that steadied you recently.",
  "What are you ready to set down today?",
  "What sound or moment brought you back to yourself?",
  "Who or what are you grateful for right now?",
  "What does showing up for yourself look like today?",
];

function getClientId() {
  let id = localStorage.getItem("cm_client_id");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2);
    localStorage.setItem("cm_client_id", id);
  }
  return id;
}

export default function DailyReflection() {
  const { user, login, logout } = useAuth();
  const deviceId = getClientId();
  const clientId = user ? user.user_id : deviceId;
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reflections/${clientId}`);
      setHistory(data);
    } catch (e) {
      console.error("history load failed", e);
      setHistory([]);
    }
  }, [clientId]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/streak/${clientId}`);
      setState(data);
    } catch (e) {
      console.error("streak load failed", e);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/streak/${clientId}/complete`, { text });
      setState(data);
      setText("");
    } catch (e) {
      console.error("complete failed", e);
    } finally {
      setBusy(false);
    }
  };

  const startDayOne = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/streak/${clientId}/reset`);
      setState(data);
    } catch (e) {
      console.error("reset failed", e);
    } finally {
      setBusy(false);
    }
  };

  const streak = state?.streak ?? 0;
  const milestone = state?.milestone ?? false;
  const completedToday = state?.completed_today ?? false;
  const promptIdx = Math.min(streak, PROMPTS.length - 1);

  return (
    <section id="reflection" className="relative py-24 md:py-32 px-6 md:px-10 border-t border-white/5" data-testid="reflection-section">
      <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(60% 60% at 80% 20%, rgba(var(--glow),0.12), transparent 60%)" }} />
      <div className="relative max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c mb-4">Daily Reflection</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">Your 7-day rhythm of care.</h2>
          <p className="mt-5 text-fg/70 md:text-lg leading-relaxed">
            A single honest reflection each day. Show up seven days in a row to complete a cycle. Miss a day and the rhythm gently starts over — no guilt, just the invitation to begin again.
          </p>

          <div className="mt-6" data-testid="reflection-account">
            {user ? (
              <div className="flex items-center gap-3 flex-wrap" data-testid="account-signed-in">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/15" />
                ) : null}
                <span className="font-mono text-xs text-fg/70" data-testid="account-name">
                  Saving to {user.name}'s account
                </span>
                <button
                  onClick={logout}
                  data-testid="sign-out-btn"
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.15em] uppercase text-fg/50 hover:text-primary-c transition-colors"
                >
                  <LogOut size={13} /> Sign out
                </button>
              </div>
            ) : (
              <button
                onClick={login}
                data-testid="sign-in-btn"
                className="inline-flex items-center gap-2 shape-r border border-primary-c/50 bg-primary-c/10 text-primary-c font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-primary-c/20 transition-colors"
              >
                <LogIn size={14} /> Sign in to save your streak across devices
              </button>
            )}
          </div>

          <div className="mt-8 flex items-center gap-4" data-testid="streak-counter">
            <span className="font-serif text-6xl md:text-7xl text-primary-c leading-none" data-testid="streak-count">{streak}</span>
            <span className="font-mono text-sm text-fg/50">/ 7 days</span>
          </div>
          <div className="mt-5 flex gap-2.5" data-testid="streak-dots">
            {Array.from({ length: 7 }).map((_, i) => (
              <span
                key={i}
                data-testid={`streak-dot-${i + 1}`}
                data-filled={i < streak}
                className="h-3.5 w-3.5 rounded-full transition-colors duration-500"
                style={{
                  backgroundColor: i < streak ? "var(--primary)" : "transparent",
                  border: `1.5px solid ${i < streak ? "var(--primary)" : "rgba(255,255,255,0.22)"}`,
                  boxShadow: i < streak ? "0 0 14px rgba(var(--glow),0.6)" : "none",
                }}
              />
            ))}
          </div>

          <Dialog onOpenChange={(open) => open && loadHistory()}>
            <DialogTrigger asChild>
              <button
                data-testid="view-reflections-btn"
                className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/60 hover:text-primary-c transition-colors"
              >
                <BookOpen size={15} /> View your reflections
              </button>
            </DialogTrigger>
            <DialogContent className="glass shape-r border-white/10 max-w-lg text-fg" data-testid="reflection-history">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl tracking-tight">Your reflections</DialogTitle>
              </DialogHeader>
              <div className="mt-2 max-h-[60vh] overflow-y-auto pr-1 space-y-4">
                {history === null ? (
                  <p className="text-fg/50 text-sm py-8 text-center">Loading…</p>
                ) : history.length === 0 ? (
                  <p className="text-fg/50 text-sm py-8 text-center" data-testid="history-empty">
                    No reflections yet. Complete today's reflection to begin your rhythm.
                  </p>
                ) : (
                  history.map((h, i) => (
                    <div key={i} data-testid="history-entry" className="border-l-2 border-primary-c/60 pl-4 py-1">
                      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-primary-c mb-1.5">{h.date}</p>
                      <p className="text-fg/80 leading-relaxed text-[15px]">{h.text}</p>
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="glass shape-r p-8 md:p-10 min-h-[300px] flex flex-col justify-center">
          <AnimatePresence mode="wait">
            {milestone ? (
              <motion.div key="milestone" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="milestone-card">
                <Sparkles className="text-primary-c mb-4" size={30} />
                <p className="font-serif text-2xl md:text-3xl leading-snug" data-testid="milestone-message">{MILESTONE_MSG}</p>
                <button
                  onClick={startDayOne}
                  disabled={busy}
                  data-testid="start-day-1-btn"
                  className="mt-8 shape-r bg-primary-c text-bg font-medium px-7 py-3.5 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  Start Day 1
                </button>
              </motion.div>
            ) : completedToday ? (
              <motion.div key="done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="completed-card">
                <div className="w-12 h-12 rounded-full bg-primary-c/20 flex items-center justify-center mb-4">
                  <Check className="text-primary-c" size={24} />
                </div>
                <p className="font-serif text-2xl md:text-3xl leading-snug">You've shown up today.</p>
                <p className="mt-3 text-fg/60 leading-relaxed">
                  Day {streak} of 7 complete. Come back tomorrow to keep your rhythm alive.
                </p>
              </motion.div>
            ) : (
              <motion.div key="prompt" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex items-center gap-2 text-primary-c mb-3">
                  <PenLine size={16} />
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase">Day {streak + 1} prompt</span>
                </div>
                <p className="font-serif text-xl md:text-2xl leading-snug mb-5">{PROMPTS[promptIdx]}</p>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a few honest words…"
                  rows={3}
                  data-testid="reflection-input"
                  className="w-full shape-r bg-black/30 border border-white/15 focus:border-primary-c focus:outline-none focus:ring-2 focus:ring-primary-c/40 p-4 text-fg placeholder:text-fg/30 resize-none transition-colors"
                />
                <button
                  onClick={complete}
                  disabled={busy}
                  data-testid="complete-reflection-btn"
                  className="mt-5 shape-r bg-primary-c text-bg font-medium px-7 py-3.5 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {busy ? "Saving…" : "Complete Today's Reflection"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
